// store/modules/user.ts
import { defineStore } from "pinia";
import { login } from "../../api/user";
import { getUserConfiguration, getDocumentTypesForTenant } from "../../api/tenant";
import { getReportConfigs } from "../../api/cases";
import { getAsset } from "../../api/asset";
import { Tenant, Tag, DocumentType } from "../../models";
import { getCookie, setCookie, deleteCookie } from "../../utils/cookies";
import { jwtDecode } from "jwt-decode";
import { ServerName } from "../../utils/constants/serverName";

const DEFAULT_STATE = {
  status: "loading",
  sidebarPinned: false,
  hasLoggedOut: false,
  token: localStorage.getItem("user-token") || "",
  refreshToken: localStorage.getItem("refresh-token") || "",
  isOAuthToken: localStorage.getItem("is-oauth-token") === "true",
  tokenRefreshTimer: null as number | null,
  isRefreshing: false,
  profile: {
    role: "PUBLIC",
    username: "",
    tenantProfile: {
      enabledFeatures: "",
      name: "",
      id: -1,
      doctypes: [],
      color: "",
    },
  },
  isTenantPage: false,
  tenantConfig: {
    id: 0,
    doctypes: "",
    name: "",
    useRdb: false,
    webCaseReportConfig: [],
    tenant: 0,
    tenantName: "",
    tagColors: null,
    color: "",
    poweredByJaasper: false,
    enabledFeatures: "",
  } as Tenant,
  tagColors: [] as string[],
  tags: [] as Tag[],
  logos: {
    large: "",
    small: "",
  },
};

const DEFAULT_FALLBACK = "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO+ip1sAAAAASUVORK5CYII=";

export const useUserStore = defineStore("user", {
  state: () => ({ ...DEFAULT_STATE, authExpired: false, isRefreshing: false, hasLoggedOut: false }),
  actions: {
    toggleSidebarPin() {
      this.sidebarPinned = !this.sidebarPinned;
    },

    // Helper to get token expiration time
    getTokenExpiration(token: string): number | null {
      try {
        const decoded: any = jwtDecode(token);
        return decoded.exp ? decoded.exp * 1000 : null;
      } catch (e) {
        console.error("Unable to decode token:", e);
        return null;
      }
    },

    // Check if tokens are expired
    checkIfTokensAreExpired(refreshToken: string): boolean {
      try {
        const decoded: any = jwtDecode(refreshToken);
        const expiration = decoded.exp ? decoded.exp * 1000 : null;
        if (!expiration) {
          return true;
        }

        const timeUntilExpiry = expiration - Date.now() - 10000;
        return timeUntilExpiry <= 0;
      } catch (e) {
        return true;
      }
    },

    // Setup automatic token refresh for OAuth tokens
    setupTokenRefresh() {
      if (!this.isOAuthToken || !this.token) {
        return;
      }

      // Clear existing timer
      if (this.tokenRefreshTimer) {
        clearTimeout(this.tokenRefreshTimer);
        this.tokenRefreshTimer = null;
      }

      const expirationTime = this.getTokenExpiration(this.token);
      if (!expirationTime) {
        console.warn("Cannot setup token refresh: no expiration time");
        return;
      }

      const now = Date.now();
      const timeUntilExpiry = expirationTime - now;
      let refreshTime = timeUntilExpiry - 60000;
      if (refreshTime > 60000) {
        refreshTime = 60000;
      }

      if (refreshTime > 0) {
        console.log(`Token refresh scheduled in ${refreshTime / 1000} seconds`);
        this.tokenRefreshTimer = window.setTimeout(() => {
          this.refreshOAuthToken();
        }, refreshTime);
      } else if (timeUntilExpiry > 0) {
        console.log("Token about to expire, scheduling immediate refresh");
        this.tokenRefreshTimer = window.setTimeout(() => {
          this.refreshOAuthToken();
        }, 1000); // Small delay to avoid immediate loop
      } else {
        // Token already expired
        console.log("Token expired, marking auth as expired");
        this.setAuthExpired();
      }
    },

    // Refresh OAuth token
    async refreshOAuthToken() {
      // Prevent concurrent refresh attempts
      if (this.isRefreshing) {
        console.log("Token refresh already in progress, skipping");
        return;
      }

      console.log("Refreshing OAuth Token");
      if (!this.refreshToken) {
        console.error("No refresh token available");
        this.setAuthExpired();
        return;
      }

      this.isRefreshing = true;

      try {
        const response = await fetch(`${ServerName}/sso/refresh/token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            refresh_token: this.refreshToken,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to refresh token");
        }

        const data = await response.json();

        this.token = data.access_token;
        this.refreshToken = data.refresh_token || this.refreshToken;

        localStorage.setItem("user-token", this.token);
        localStorage.setItem("refresh-token", this.refreshToken);

        console.log("Token refreshed successfully");
        this.setupTokenRefresh();
      } catch (error) {
        console.error("Error refreshing token:", error);
        this.setAuthExpired();
      } finally {
        this.isRefreshing = false;
      }
    },

    // Handle OAuth login from cookies
    async handleOAuthCallback() {
      const token = getCookie("access_token");
      const refreshToken = getCookie("refresh_token");

      if (!token) {
        return false;
      }

      // Check if tokens are expired before proceeding
      if (refreshToken && this.checkIfTokensAreExpired(refreshToken)) {
        console.log("OAuth tokens expired, clearing cookies");
        deleteCookie("access_token");
        deleteCookie("refresh_token");
        return false;
      }

      try {
        const decoded = jwtDecode(token);
        console.log("OAuth token decoded:", decoded);

        this.token = token;
        this.refreshToken = refreshToken || "";
        this.isOAuthToken = true;
        this.status = "success";

        localStorage.setItem("user-token", token);
        localStorage.setItem("refresh-token", this.refreshToken);
        localStorage.setItem("is-oauth-token", "true");

        // Delete cookies immediately after storing in localStorage
        deleteCookie("access_token");
        deleteCookie("refresh_token");

        await this.userRequest();

        // Setup token refresh AFTER userRequest completes
        this.setupTokenRefresh();

        return true;
      } catch (e) {
        console.error("Error handling OAuth callback:", e);
        deleteCookie("access_token");
        deleteCookie("refresh_token");
        return false;
      }
    },

    async authRequest(user: { username: string; password: string }) {
      this.status = "loading";
      this.authExpired = false;
      try {
        const token = await login(user.username, user.password);
        localStorage.setItem("user-token", token);
        localStorage.setItem("is-oauth-token", "false");
        this.status = "success";
        this.token = token;
        this.isOAuthToken = false;
        this.refreshToken = "";
        await this.userRequest();
      } catch (err) {
        this.status = "error";
        this.token = "";
        localStorage.removeItem("user-token");
        localStorage.removeItem("is-oauth-token");
      }
    },

    async userIdle() {
      this.status = "idle";
    },

    async loadLogos(tenantId: number) {
      try {
        const logoData = await getAsset("logo_large", tenantId);
        if (logoData && logoData !== DEFAULT_FALLBACK) {
          this.logos.large = logoData;
        }
      } catch (e) {
        console.warn("Error loading tenant logo:", e);
      }

      try {
        const logoSmallData = await getAsset("logo_small", tenantId);
        if (logoSmallData && logoSmallData !== DEFAULT_FALLBACK) {
          this.logos.small = logoSmallData;
        }
      } catch (e) {
        console.warn("Error loading tenant small logo:", e);
      }
    },

    async userRequest() {
      this.status = "loading";
      try {
        const userConfig = await getUserConfiguration();
        const tenantConfig = userConfig.tenantProfile;

        const [doctypes, webCaseReportConfigs] = await Promise.all([
          getDocumentTypesForTenant(userConfig.tenant),
          getReportConfigs(userConfig.tenantProfile.id.toString()).catch(() => []),
          this.loadLogos(tenantConfig.id), // Load logos as part of user initialization
        ]);

        tenantConfig.doctypes = doctypes;

        try {
          tenantConfig.webCaseReportConfigs = await webCaseReportConfigs;
        } catch {
          tenantConfig.webCaseReportConfigs = [];
        }

        this.profile = userConfig;
        this.profile.tenantProfile = tenantConfig;
        this.tagColors = JSON.parse(tenantConfig.tagColors || "[]");

        this.status = "success";
      } catch {
        this.status = "error";
        this.token = "";
      }
    },

    setTenantPage(isTenantPage: boolean) {
      this.isTenantPage = isTenantPage;
    },

    resetTenantConfig() {
      this.isTenantPage = false;
      this.tags = [];
      this.tenantConfig = {
        id: 0,
        doctypes: "",
        name: "",
        useRdb: false,
        webCaseReportConfig: [],
        tenant: 0,
        tenantName: "",
        tagColors: null,
        color: "",
        poweredByJaasper: false,
        enabledFeatures: "",
      };
    },

    async authLogout() {
      // Clear refresh timer
      if (this.tokenRefreshTimer) {
        clearTimeout(this.tokenRefreshTimer);
        this.tokenRefreshTimer = null;
      }

      Object.assign(this.profile, DEFAULT_STATE.profile);
      Object.assign(this.tenantConfig, DEFAULT_STATE.tenantConfig);
      Object.assign(this.logos, DEFAULT_STATE.logos);

      this.token = "";
      this.refreshToken = "";
      this.isOAuthToken = false;
      this.isRefreshing = false;
      this.resetTenantConfig();

      this.hasLoggedOut = true;

      localStorage.removeItem("user-token");
      localStorage.removeItem("refresh-token");
      localStorage.removeItem("is-oauth-token");
      deleteCookie("access_token");
      deleteCookie("refresh_token");
      return Promise.resolve();
    },
    resetLogoutFlag() {
      this.hasLoggedOut = false;
    },
    setAuthExpired() {
      // Clear refresh timer
      if (this.tokenRefreshTimer) {
        clearTimeout(this.tokenRefreshTimer);
        this.tokenRefreshTimer = null;
      }

      this.authExpired = true;
      this.isRefreshing = false;
      Object.assign(this.profile, DEFAULT_STATE.profile);
      Object.assign(this.tenantConfig, DEFAULT_STATE.tenantConfig);
      Object.assign(this.logos, DEFAULT_STATE.logos);

      this.token = "";
      this.refreshToken = "";
      this.isOAuthToken = false;
      this.resetTenantConfig();
      localStorage.removeItem("user-token");
      localStorage.removeItem("refresh-token");
      localStorage.removeItem("is-oauth-token");
      deleteCookie("access_token");
      deleteCookie("refresh_token");
    },

    resetAuthExpired() {
      this.authExpired = false;
    },
  },

  getters: {
    isAuthenticated: (state) => !!state.token && state.status == "success",
    isSidebarPinned: (state) => state.sidebarPinned,
    isUserReady: (state) => !!state.token && state.profile?.tenantProfile?.id !== -1 && state.profile?.tenantProfile?.enabledFeatures !== "",

    getProfile: (state) => state.profile,
    tenantId: (state) => state.profile.tenantProfile.id,
    getToken: (state) => state.token,
    authStatus: (state) => state.status,
    tenantFeatures: (state) => state.profile?.tenantProfile?.enabledFeatures || "",
    getTenantConfig: (state) => state.profile.tenantProfile,
    tenantName: (state) => state.profile.tenantProfile.name,

    logoLarge: (state) => (state.logos.large ? `data:image/png;base64,${state.logos.large}` : ""),
    logoSmall: (state) => (state.logos.small ? `data:image/png;base64,${state.logos.small}` : ""),

    docTypesList: (state) =>
      state.profile.tenantProfile.doctypes
        ? (state.profile.tenantProfile.doctypes as DocumentType[]).map((doc) => ({
            value: doc.type_id,
            label: doc.type_description,
            key: doc.id,
          }))
        : [],

    getLLMServers: (state): { [key: string]: string } => {
      const features = state.profile?.tenantProfile?.enabledFeatures;
      if (!features) return {};
      const urlPattern = /llm\.(\w+):([a-zA-Z0-9.-]+)/g;
      const urls: { [key: string]: string } = {};
      let match;
      while ((match = urlPattern.exec(features)) !== null) {
        urls[match[1]] = match[2];
      }
      return urls;
    },
  },
  persist: {
    key: "user-store",
    storage: localStorage,
    pick: ["token", "sidebarPinned", "refreshToken", "isOAuthToken"],
  },
});
